import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

import React, { useState, useEffect, useMemo, useCallback } from "react";

// global chart setting
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
  {
    id: "drawVerticalLinePlugin",
    afterDraw: function (chart: any) { // eslint-disable-line
      if (chart.tooltip._active && chart.tooltip._active.length) {
        const activePoint = chart.tooltip._active[0];
        const ctx = chart.ctx;
        const x = activePoint.element.x;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#515767";
        ctx.stroke();
        ctx.restore();
      }
    }
  },
  {
    id: "detectChartAreaChangePlugin",
    beforeEvent(chart: any, args:any) { // eslint-disable-line
      if (args.inChartArea === true && chart.titleBlock.options.text === "Results") {
        args.event.native.target.style.cursor = "pointer";
      }
      if (args.inChartArea === false && chart.titleBlock.options.text === "Results") {
        args.event.native.target.style.cursor = "default";
      }
    }
  }
);

let logoutTimer: null | ReturnType<typeof setTimeout> = null;

export interface IUserTenant {
  id: string,
  name: string,
  roles: string[]
}

export interface ISelectedTenant {
  selected_tenant: string, 
  selected_tenant_id: string,
  roles: string[],
  tenants?: IUserTenant[]
}

export interface IOnboardingAllData {
  dataset: number | undefined ,
  modelconfig: number | undefined, 
  report: number | undefined, 
  runmodeconfig: number | undefined, 
  tmsconfig: number | undefined,
  database_connection: number | undefined, 
  entity: number | undefined, 
  enumeration: number | undefined, 
  event: number | undefined,
  datamodel_report: number | undefined,
  screen: number | undefined,
  action: number | undefined,
  domain_type: number | undefined,
  event_capture: number | undefined,
  entity_capture: number | undefined,
  [index: string]: number | undefined;
}

export interface IOnBoardingAnalytics {
  dataset: number | undefined,
  modelconfig: number | undefined, 
  report: number | undefined, 
  runmodeconfig: number | undefined, 
  tmsconfig: number | undefined,
  template: number | undefined
  tenant_template: number | undefined
}

export interface IOnBoardingManager {
  screen: number | undefined,
  action: number | undefined,
  domain_type: number | undefined,
  event_capture: number | undefined,
  entity_capture: number | undefined,
}

export interface IOnBoardingPlatform {
  database_connection: number | undefined, 
  entity: number | undefined, 
  enumeration: number | undefined,
  event: number | undefined,
  alertcase: number | undefined,
  token: number | undefined,
  datamodel_report: number | undefined
}

export interface IContext {
  systemAdmin: boolean,
  token: string,
  userUpdateStatus: boolean,
  isLoggedIn: boolean,
  selectedTenant: ISelectedTenant,
  tenants?: IUserTenant[],
  userId: string,
  email: string,
  name: string,
  lastName: string,
  onboardingAnalyticsToggle: boolean,
  onboardingPlatformToggle: boolean,
  onboardingManagerToggle: boolean,
  onboardingAnalyticsData: IOnBoardingAnalytics,
  setOnboardingAnalyticsHandler: (data: IOnBoardingAnalytics) => void,
  onboardingPlatformData: IOnBoardingPlatform,
  setOnboardingPlatformHandler: (data: IOnBoardingPlatform) => void,
  onboardingManagerData: IOnBoardingManager,
  setOnboardingManagerHandler: (data: IOnBoardingManager) => void,

  setOnboardingAnalyticsToggleHandler: () => void,
  setOnboardingPlatformToggleHandler: () => void,
  setOnboardingManagerToggleHandler: () => void,
  userUpdate: () => void,
  logout: () => void,
  selectUserData: (adminStatus: boolean, email: string, name: string, lastName: string, tenants?: IUserTenant[]) => void,
  selectTenant: (id: string, name: string, roles: string[]) => void,
  login: (token: string, expirationTime: string, changePassword: boolean, userId: string) => void
}
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthContext = React.createContext<IContext>({
  systemAdmin: false,
  token: "",
  userId: "",
  isLoggedIn: false,
  userUpdateStatus: false,
  selectedTenant: {selected_tenant: "", selected_tenant_id: "", roles: []},
  email: "",
  name: "",
  lastName: "",

  onboardingAnalyticsToggle: false,
  onboardingPlatformToggle: false,
  onboardingManagerToggle: false,
  onboardingPlatformData: {
    database_connection: undefined,
    token: undefined,
    alertcase: undefined,
    entity: undefined,
    enumeration: undefined,
    event: undefined,
    datamodel_report: undefined
  },
  setOnboardingPlatformHandler: () => undefined,
  setOnboardingPlatformToggleHandler: () => undefined,
  onboardingAnalyticsData: {
    dataset: undefined,
    modelconfig: undefined,
    report: undefined,
    runmodeconfig: undefined,
    tmsconfig: undefined,
    template: undefined,
    tenant_template: undefined
  },
  setOnboardingAnalyticsHandler: () => undefined,
  setOnboardingAnalyticsToggleHandler: () => undefined,
  onboardingManagerData: {
    screen: undefined,
    action: undefined,
    domain_type: undefined,
    event_capture: undefined,
    entity_capture: undefined
  },
  setOnboardingManagerHandler: () => undefined,
  setOnboardingManagerToggleHandler: () => undefined,

  userUpdate: () => undefined,
  login: () => undefined,
  logout: () => undefined,
  selectTenant: () => undefined,
  selectUserData: () => undefined,
});

const calculateRemainingTime = (expirationTime: string) => {
  const formatedCurrentTime = new Date().getTime();
  const formatedExpirationTime = new Date(expirationTime).getTime();
  const remainingDuration = formatedExpirationTime - formatedCurrentTime;
  return remainingDuration;
};

const retriveStoredToken = () => {
  const storedToken = localStorage.getItem("token");
  const storedExpiration = localStorage.getItem("expirationTime");
  const remainingTime = storedExpiration && calculateRemainingTime(storedExpiration);

  if (remainingTime && remainingTime <= 3600) {
    localStorage.removeItem("token");
    localStorage.removeItem("expirationTime");
    return "";
  }

  return {
    token: storedToken,
    duration: remainingTime,
  };
};

const retriveTenant = () => {
  const storedTenantName = localStorage.getItem("selected_tenant");
  const storedTenantId = localStorage.getItem("selected_tenant_id");
  const storedRoles = localStorage.getItem("roles");

  if (!storedTenantName || !storedTenantId || !storedRoles) {
    return {selected_tenant: "", selected_tenant_id: "", roles: []};
  }
  return {selected_tenant: storedTenantName, selected_tenant_id: storedTenantId, roles: JSON.parse(storedRoles)};
};

const retriveUserId = () => {
  const storedUserId = localStorage.getItem("userId");
  if (!storedUserId) {
    return  "";
  }
  return storedUserId;
};

export const AuthContextProvider = ({ children }: AuthProviderProps) => {
  const tokenData = useMemo(() => retriveStoredToken(), []);
  const tenantData  = useMemo(() => retriveTenant(), []);
  const userIdData  = useMemo(() => retriveUserId(), []);

  let initialUserId = "";
  if (userIdData) {
    initialUserId = userIdData;
  }

  let initialToken = "";
  if (tokenData && tokenData.token) {
    initialToken = tokenData.token;
  }
  
  let initialTenant:ISelectedTenant = {selected_tenant: "", selected_tenant_id: "", roles: []};
  if (tenantData && tenantData.selected_tenant_id !== "") {
    initialTenant = tenantData;
  }

  const [token, setToken] = useState(initialToken);
  const [admin, setAdmin] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tenants, setTenants] = useState<undefined | IUserTenant[]>(undefined);
  const [tenant, setTenant] = useState<ISelectedTenant>(initialTenant);
  const [userId, setUserId] = useState(initialUserId);

  const [onboardingAnalyticsData, setOnboardingAnalyliticsData] = useState<IOnBoardingAnalytics>({dataset: undefined, modelconfig: undefined, report: undefined, runmodeconfig: undefined, tmsconfig: undefined, template: undefined, tenant_template: undefined});
  const [onboardingPlatformData, setOnboardingPlatformData] = useState<IOnBoardingPlatform>({database_connection: undefined, token: undefined, alertcase: undefined, entity: undefined, enumeration: undefined, event: undefined, datamodel_report: undefined});
  const [onboardingManagerData, setOnboardingManagerData] = useState<IOnBoardingManager>({screen: undefined, action: undefined, domain_type: undefined, event_capture: undefined, entity_capture: undefined});
  const [onboardingAnalyticsToggle, setOnboardingAnalyticsToggle] = useState(false);
  const [onboardingPlatformToggle, setOnboardingPlatformToggle] = useState(false);
  const [onboardingManagerToggle, setOnboardingManagerToggle] = useState(false);

  const [userUpdateStatus, setUserUpdateStatus] = useState(false);
  const userIsLoggedIn = !!token;

  const tenantSelectHandler = (id: string, name: string, roles: string[]) => {
    localStorage.setItem("selected_tenant", name);
    localStorage.setItem("selected_tenant_id", id);
    localStorage.setItem("roles", JSON.stringify(roles));
    setTenant({selected_tenant: name, selected_tenant_id: id, roles: roles});
  };

  const loginHandler = (token: string, expirationTime: string, changePassword: boolean, userId: string) => {
    setToken(token);
    setUserId(userId);
    localStorage.setItem("token", token);
    localStorage.setItem("expirationTime", expirationTime);
    localStorage.setItem("userId", userId);
    if (changePassword) {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
    }
    const remainingTime = calculateRemainingTime(expirationTime);
    logoutTimer = setTimeout(logoutHandler, remainingTime);
  };

  const logoutHandler = useCallback(() => {
    setToken("");
    localStorage.removeItem("token");
    localStorage.removeItem("expirationTime");

    setTenant({selected_tenant: "", selected_tenant_id: "", roles: []});
    localStorage.removeItem("roles");
    localStorage.removeItem("selected_tenant");
    localStorage.removeItem("selected_tenant_id");

    setUserId("");
    localStorage.removeItem("userId");
    
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  useEffect(() => {
    if (tokenData && tokenData.duration) {
      logoutTimer = setTimeout(logoutHandler, tokenData.duration);
    }
  }, [tokenData, logoutHandler]);

  const userUpdateHandler = () => {
    setUserUpdateStatus(prev => !prev);
  };

  const selectUserDataHandler = (adminStatus: boolean, email: string, name: string, lastName: string, tenantsLoc?: IUserTenant[]) => {
    setAdmin(adminStatus);
    setEmail(email);
    setName(name);
    setLastName(lastName);
    tenantsLoc && setTenants(tenantsLoc);
  };

  const setOnboardingAnalyticsHandler = (data: IOnBoardingAnalytics) => {
    setOnboardingAnalyliticsData({...onboardingAnalyticsData, ...data});
  };

  const setOnboardingPlatformHandler = (data: IOnBoardingPlatform) => {
    setOnboardingPlatformData({...onboardingPlatformData, ...data});
  };

  const setOnboardingManagerHandler = (data: IOnBoardingManager) => {
    setOnboardingManagerData({...onboardingManagerData, ...data});
  };

  const setOnboardingAnalyticsToggleHandler = () => {
    setOnboardingAnalyticsToggle(prev => !prev);
  };

  const setOnboardingPlatformToggleHandler = () => {
    setOnboardingPlatformToggle(prev => !prev);
  };

  const setOnboardingManagerToggleHandler = () => {
    setOnboardingManagerToggle(prev => !prev);
  };

  const contextValue: IContext = {
    systemAdmin: admin,
    userUpdateStatus,
    token,
    userId,
    email,
    name,
    lastName,
    tenants,
    onboardingAnalyticsToggle,
    onboardingPlatformToggle,
    onboardingManagerToggle,
    selectedTenant: tenant,
    isLoggedIn: userIsLoggedIn,
    onboardingAnalyticsData: onboardingAnalyticsData,
    onboardingPlatformData,
    onboardingManagerData,
    setOnboardingPlatformHandler,
    setOnboardingAnalyticsHandler,
    setOnboardingManagerHandler,
    setOnboardingAnalyticsToggleHandler,
    setOnboardingPlatformToggleHandler,
    setOnboardingManagerToggleHandler,
    login: loginHandler,
    logout: logoutHandler,
    selectTenant: tenantSelectHandler,
    userUpdate: userUpdateHandler,
    selectUserData: selectUserDataHandler
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;